import React, { useState, useEffect } from 'react';
import styles from '../../styles/PantallaDeProgramasAcademicos.v2.module.css';
import { getCampus } from '../../api/campus';

function NewBecaModal({ isOpen, onClose, onSave, editMode = false, becaData = null }) {
  const [formData, setFormData] = useState({
    nombreBeca: '',
    codigoBeca: '',
    tipoBeca: '',
    estado: 'ACTIVO',
    descripcion: '',
    porcentajeMaximo: '',
    montoMaximo: '',
    seccionesAplicables: [],
    requisitos: '',
    fondoAsignado: '',
    idCampus: ''
  });

  const [selectedSecciones, setSelectedSecciones] = useState([]);
  const [errors, setErrors] = useState({});
  const [showValidationMessage, setShowValidationMessage] = useState(false);
  const [campusList, setCampusList] = useState([]);
  const [loadingCampus, setLoadingCampus] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Cargar lista de campus al montar
  useEffect(() => {
    const fetchCampus = async () => {
      setLoadingCampus(true);
      try {
        const data = await getCampus();
        setCampusList(data || []);
      } catch (error) {
        console.error('Error al cargar campus:', error);
        setCampusList([]);
      } finally {
        setLoadingCampus(false);
      }
    };
    fetchCampus();
  }, []);

  // Función para limpiar el marcador de secciones de la descripción
  const cleanDescripcionFromSecciones = (descripcion) => {
    if (!descripcion) return '';
    // Remover el marcador [SECCIONES:...] de la descripción
    const regex = /\s*\[SECCIONES:[^\]]*\]\s*/g;
    return descripcion.replace(regex, '').trim();
  };

  // Cargar datos cuando está en modo edición
  useEffect(() => {
    if (editMode && becaData) {
      // DEBUG: Mostrar datos recibidos
      console.log('=== DEBUG CARGA BECA ===');
      console.log('becaData completo:', becaData);
      console.log('porcentaje_maximo recibido:', becaData.porcentaje_maximo, 'tipo:', typeof becaData.porcentaje_maximo);
      console.log('monto_maximo recibido:', becaData.monto_maximo, 'tipo:', typeof becaData.monto_maximo);
      
      // Limpiar la descripción del marcador de secciones
      const descripcionLimpia = cleanDescripcionFromSecciones(becaData.descripcion);
      
      setFormData({
        nombreBeca: becaData.nombre || '',
        codigoBeca: becaData.codigo || '',
        tipoBeca: becaData.tipo_de_beca || '',
        estado: becaData.estado || 'ACTIVO',
        descripcion: typeof becaData.descripcion === 'string' ? becaData.descripcion : (becaData.descripcion !== undefined && becaData.descripcion !== null ? String(becaData.descripcion) : ''),
        porcentajeMaximo: becaData.porcentaje_maximo !== undefined && becaData.porcentaje_maximo !== null ? (typeof becaData.porcentaje_maximo === 'number' ? becaData.porcentaje_maximo.toFixed(2) : String(becaData.porcentaje_maximo)) : '',
        montoMaximo: becaData.monto_maximo !== undefined && becaData.monto_maximo !== null ? (typeof becaData.monto_maximo === 'number' ? becaData.monto_maximo.toFixed(2) : String(becaData.monto_maximo)) : '',
        seccionesAplicables: becaData.secciones_aplicables || [],
        requisitos: becaData.requisitos || '',
        fondoAsignado: becaData.fondo_asignado !== undefined && becaData.fondo_asignado !== null ? (typeof becaData.fondo_asignado === 'number' ? becaData.fondo_asignado.toFixed(2) : String(becaData.fondo_asignado)) : (becaData.fondo_asignado === 0 ? '0.00' : ''),
        idCampus: becaData.campus_id || ''
      });
      setSelectedSecciones(becaData.secciones_aplicables || []);
      setErrors({});
      setShowValidationMessage(false);
    } else {
      // Reset form when not in edit mode
      setFormData({
        nombreBeca: '',
        codigoBeca: '',
        tipoBeca: '',
        estado: 'ACTIVO',
        descripcion: '',
        porcentajeMaximo: '',
        montoMaximo: '',
        seccionesAplicables: [],
        requisitos: '',
        fondoAsignado: '',
        idCampus: ''
      });
      setSelectedSecciones([]);
      setErrors({});
      setShowValidationMessage(false);
    }
  }, [editMode, becaData, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Lógica XOR para porcentaje y monto (crear y editar):
    // si se llena uno, se limpia el otro
    if (name === 'porcentajeMaximo' && value) {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        montoMaximo: '' // Limpiar monto si se llena porcentaje
      }));
      // Limpiar error si existía
      if (errors.porcentajeMaximo || errors.montoMaximo) {
        setErrors(prev => ({ ...prev, porcentajeMaximo: null, montoMaximo: null }));
      }
      return;
    }
    
    if (name === 'montoMaximo' && value) {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        porcentajeMaximo: '' // Limpiar porcentaje si se llena monto
      }));
      // Limpiar error si existía
      if (errors.porcentajeMaximo || errors.montoMaximo) {
        setErrors(prev => ({ ...prev, porcentajeMaximo: null, montoMaximo: null }));
      }
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Limpiar error del campo cuando el usuario escribe
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSeccionChange = (e) => {
    const value = e.target.value;
    if (value && value !== '' && !selectedSecciones.includes(value)) {
      const newSecciones = [...selectedSecciones, value];
      setSelectedSecciones(newSecciones);
      setFormData(prev => ({
        ...prev,
        seccionesAplicables: newSecciones
      }));
    }
    e.target.value = '';
  };

  const handleSeccionRemove = (seccion) => {
    const newSecciones = selectedSecciones.filter(s => s !== seccion);
    setSelectedSecciones(newSecciones);
    setFormData(prev => ({
      ...prev,
      seccionesAplicables: newSecciones
    }));
  };

  // Validar formulario
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nombreBeca.trim()) {
      newErrors.nombreBeca = 'El nombre de la beca es obligatorio';
    }
    
    if (!formData.codigoBeca.trim()) {
      newErrors.codigoBeca = 'El código de la beca es obligatorio';
    }
    
    if (!formData.tipoBeca) {
      newErrors.tipoBeca = 'Debe seleccionar un tipo de beca';
    }
    
    // Campus obligatorio (solo para nuevas becas, para edición es opcional por compatibilidad)
    if (!editMode && !formData.idCampus) {
      newErrors.idCampus = 'Debe seleccionar un campus';
    }
    
    // Validación de porcentaje (si tiene valor)
    if (formData.porcentajeMaximo) {
      const porcentaje = parseFloat(formData.porcentajeMaximo);
      if (isNaN(porcentaje) || porcentaje < 0 || porcentaje > 100) {
        newErrors.porcentajeMaximo = 'El porcentaje debe estar entre 0 y 100';
      }
    }
    
    // Validación XOR: solo uno de porcentaje o monto puede tener valor
    // Se aplica SIEMPRE (crear y editar) - al editar, el usuario debe limpiar uno de los campos
    const tienePorcentaje = formData.porcentajeMaximo && parseFloat(formData.porcentajeMaximo) > 0;
    const tieneMonto = formData.montoMaximo && parseFloat(formData.montoMaximo) > 0;
    
    if (tienePorcentaje && tieneMonto) {
      newErrors.porcentajeMaximo = 'Solo puede especificar porcentaje O monto máximo, no ambos. Por favor, elimine uno de los valores.';
      newErrors.montoMaximo = 'Solo puede especificar porcentaje O monto máximo, no ambos. Por favor, elimine uno de los valores.';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Evitar doble submit
    if (isSaving) return;

    // Validar formulario
    if (!validateForm()) {
      setShowValidationMessage(true);
      return;
    }
    
    setShowValidationMessage(false);
    
    // Mapear campos del formulario a los nombres esperados por el backend
    const backendData = {
      nombre_beca: formData.nombreBeca.trim(),
      codigo_beca: formData.codigoBeca.trim(),
      tipo_beca: formData.tipoBeca.toUpperCase(),
      descripcion: formData.descripcion || '',
      // Solo enviar porcentaje si tiene valor, de lo contrario null
      porcentaje_maximo: formData.porcentajeMaximo ? parseFloat(formData.porcentajeMaximo) : null,
      // Solo enviar monto si tiene valor, de lo contrario null
      monto_maximo_input: formData.montoMaximo ? parseFloat(formData.montoMaximo) : null,
      fondo_asignado: formData.fondoAsignado ? parseFloat(formData.fondoAsignado) : null,
      requisitos: formData.requisitos || '',
      secciones_input: selectedSecciones,
      // Agregar campus al payload
      id_campus: formData.idCampus ? parseInt(formData.idCampus) : null,
      activo: formData.estado === 'ACTIVO'
    };
    
    try {
      setIsSaving(true);
      await onSave(backendData);
    } catch (err) {
      // El error ya fue manejado/mostrado en el padre; solo liberamos el botón
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  // Helpers de visualización: si el valor es exactamente 0, mostrarlo como "Deshabilitado"
  const porcentajeEsCero = formData.porcentajeMaximo !== ''
    && !isNaN(parseFloat(formData.porcentajeMaximo))
    && parseFloat(formData.porcentajeMaximo) === 0;

  const montoEsCero = formData.montoMaximo !== ''
    && !isNaN(parseFloat(formData.montoMaximo))
    && parseFloat(formData.montoMaximo) === 0;

  return (
    <div className={styles.modalBackdrop} role="presentation" onMouseDown={(e) => {
      if (e.target === e.currentTarget) onClose?.();
    }}>
      <div className={styles.modalContainer} role="dialog" aria-modal="true" aria-labelledby="beca-title">
        <div className={styles.modalHeader}>
          <h2 id="beca-title" className={styles.modalTitle}>{editMode ? 'Editar Beca' : 'Nueva Beca'}</h2>
          <button className={styles.modalClose} aria-label="Cerrar" onClick={onClose}>✕</button>
        </div>
        <form className={styles.modalBody} onSubmit={handleSubmit}>
          {showValidationMessage && Object.keys(errors).length > 0 && (
            <div className={styles.validationAlert}>
              <span>⚠️ Por favor, complete todos los campos obligatorios marcados con *</span>
            </div>
          )}
          <div className={styles.modalGrid}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Nombre de beca <span className={styles.required}>*</span></label>
              <input className={styles.input} name="nombreBeca" value={formData.nombreBeca} onChange={handleInputChange} required />
              {errors.nombreBeca && <span className={styles.errorText}>{errors.nombreBeca}</span>}
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Código de beca <span className={styles.required}>*</span></label>
              <input className={styles.input} name="codigoBeca" value={formData.codigoBeca} onChange={handleInputChange} required />
              {errors.codigoBeca && <span className={styles.errorText}>{errors.codigoBeca}</span>}
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Tipo de beca <span className={styles.required}>*</span></label>
              <select className={styles.select} name="tipoBeca" value={formData.tipoBeca} onChange={handleInputChange} required style={{ color: '#222' }}>
                <option value="">— Seleccionar tipo —</option>
                <option value="ADMINISTRATIVA">Administrativa</option>
                <option value="EMBAJADOR">Embajador</option>
                <option value="SOCIOECONOMICA">Socioeconómica</option>
                <option value="CULTURAL">Cultural</option>
                <option value="ACADEMICA">Académica</option>
                <option value="DEPORTIVA">Deportiva</option>
              </select>
              {errors.tipoBeca && <span className={styles.errorText}>{errors.tipoBeca}</span>}
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Estado</label>
              <div className={styles.statusSelectWrapper}>
                <span className={`${styles.statusDot} ${formData.estado === 'ACTIVO' ? styles.dotGreen : styles.dotRed}`} />
                <select
                  className={`${styles.statusSelect} ${formData.estado === 'ACTIVO' ? styles.activeSelect : styles.inactiveSelect}`}
                  name="estado"
                  value={formData.estado}
                  onChange={handleInputChange}
                >
                  <option value="ACTIVO">ACTIVO</option>
                  <option value="INACTIVO">INACTIVO</option>
                </select>
              </div>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Campus {!editMode && <span className={styles.required}>*</span>}</label>
              <select 
                className={styles.select} 
                name="idCampus" 
                value={formData.idCampus} 
                onChange={handleInputChange} 
                required={!editMode}
                style={{ color: '#222' }}
                disabled={loadingCampus}
              >
                <option value="">— Seleccionar campus —</option>
                {campusList.map((campus) => (
                  <option key={campus.id} value={campus.id}>
                    {campus.nombre}
                  </option>
                ))}
              </select>
              {errors.idCampus && <span className={styles.errorText}>{errors.idCampus}</span>}
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Descripción</label>
              <textarea className={styles.textarea} rows={2} name="descripcion" value={formData.descripcion} onChange={handleInputChange} />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Porcentaje máximo de beca (%)</label>
              <input 
                className={styles.input} 
                name="porcentajeMaximo" 
                value={porcentajeEsCero ? '' : formData.porcentajeMaximo} 
                onChange={handleInputChange} 
                inputMode="numeric"
                disabled={formData.montoMaximo && parseFloat(formData.montoMaximo) > 0}
                placeholder={
                  formData.montoMaximo && parseFloat(formData.montoMaximo) > 0
                    ? 'Deshabilitado (monto seleccionado)'
                    : (porcentajeEsCero ? 'Deshabilitado' : '')
                }
              />
              {errors.porcentajeMaximo && <span className={styles.errorText}>{errors.porcentajeMaximo}</span>}
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Monto máximo</label>
              <input 
                className={styles.input} 
                name="montoMaximo" 
                value={montoEsCero ? '' : formData.montoMaximo} 
                onChange={handleInputChange} 
                inputMode="numeric"
                disabled={formData.porcentajeMaximo && parseFloat(formData.porcentajeMaximo) > 0}
                placeholder={
                  formData.porcentajeMaximo && parseFloat(formData.porcentajeMaximo) > 0
                    ? 'Deshabilitado (porcentaje seleccionado)'
                    : (montoEsCero ? 'Deshabilitado' : '')
                }
              />
              {errors.montoMaximo && <span className={styles.errorText}>{errors.montoMaximo}</span>}
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Secciones aplicables</label>
              <select className={styles.select} onChange={handleSeccionChange} defaultValue="">
                <option value="">— Seleccionar sección —</option>
                <option value="Licenciatura">Licenciatura</option>
                <option value="Maestría">Maestría</option>
                <option value="Doctorado">Doctorado</option>
                <option value="Preparatoria">Preparatoria</option>
              </select>
              <div className={styles.chipContainer}>
                {selectedSecciones.map((seccion, index) => (
                  <div key={index} className={styles.chip}>
                    <span>{seccion}</span>
                    <button type="button" className={styles.chipRemove} onClick={() => handleSeccionRemove(seccion)}>×</button>
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Requisitos</label>
              <textarea className={styles.textarea} rows={2} name="requisitos" value={formData.requisitos} onChange={handleInputChange} />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Fondo asignado</label>
              <input className={styles.input} name="fondoAsignado" value={formData.fondoAsignado} onChange={handleInputChange} inputMode="numeric" />
            </div>
          </div>
          <div className={styles.modalActions}>
            <button type="submit" className={styles.primaryButton} disabled={isSaving}>
              {isSaving ? 'GUARDANDO...' : 'GUARDAR'}
            </button>
            <button type="button" className={styles.secondaryButton} onClick={onClose} disabled={isSaving}>CANCELAR</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default NewBecaModal;
